import { getLocalStorageJson, setLocalStorageJson } from '$lib/utils/local-storage';
import type { BaseGameSession, GameSession, PublicGameSession } from './types';
import { AnalyticsEvents, analyticsEvent } from '$lib/analytics';

const HIGHEST_SCORE_GAME_SESSION_StORAGE_KEY = 'heyharper_highest_score_game_session';
const IS_FIRST_REDEMPTION_STORAGE_KEY = 'heyharper_is_first_redemption';
export const SHARED_GAME_SESSION_ID_QUERY_PARAM_NAME = 'share';

const saveGameSession = (storageKey: string, gameSession: GameSession) => {
  setLocalStorageJson(storageKey, gameSession);
  return gameSession;
};

const saveHighestScoreGameSession = (gameSession: GameSession) => {
  return saveGameSession(HIGHEST_SCORE_GAME_SESSION_StORAGE_KEY, gameSession);
};

const getGameSession = (storageKey: string) => {
  return getLocalStorageJson(storageKey) as GameSession | null;
};

export const getHighestScoreGameSession = () => {
  return getGameSession(HIGHEST_SCORE_GAME_SESSION_StORAGE_KEY) as GameSession | null;
};

export const createGameSession = async ({
  gamePageId,
  storeRegion
}: {
  gamePageId: string;
  storeRegion: string;
}) => {
  // TODO: consider passing the region and language as path parameters
  const res = await fetch('/api/game-session', {
    method: 'POST',
    body: JSON.stringify({
      gamePageId,
      storeRegion
    })
  });

  if (!res.ok) throw new Error('Failed to create game session');

  const gameSession: GameSession = await res.json();

  return gameSession;
};

export const finishGame = async (gameSession: BaseGameSession, score: number) => {
  let highestScoreGameSession: GameSession | null = getGameSession(
    HIGHEST_SCORE_GAME_SESSION_StORAGE_KEY
  );

  if (gameSession.id === highestScoreGameSession?.id) {
    throw new Error('Trying to finish the same game session as highestScoreGameSession');
  }

  const res = await fetch(`/api/game-session/${gameSession.id}`, {
    method: 'POST',
    body: JSON.stringify({
      id: gameSession.id,
      ownershipToken: gameSession.ownershipToken,
      score,
      highestScoreGameSession: highestScoreGameSession
        ? {
            id: highestScoreGameSession.id,
            ownershipToken: highestScoreGameSession.ownershipToken
          }
        : null
    })
  });
  if (!res.ok) throw new Error('Could not finish the game!');

  const responseData = await res.json();

  if (responseData.isNewHighscore) {
    highestScoreGameSession = saveHighestScoreGameSession({
      ...gameSession,
      ...responseData
    });
  }

  analyticsEvent({
    event: AnalyticsEvents.GAME_OVER,
    score: score,
    best_score: highestScoreGameSession?.score || score
  });

  return highestScoreGameSession;
};

export const redeemDiscount = async (email: string, hasApprovedMarketingConsent?: boolean) => {
  let isFirstRedemption = getLocalStorageJson(IS_FIRST_REDEMPTION_STORAGE_KEY);
  isFirstRedemption = typeof isFirstRedemption === 'boolean' ? isFirstRedemption : true;

  const highestScoreGameSession = getGameSession(HIGHEST_SCORE_GAME_SESSION_StORAGE_KEY);

  if (!highestScoreGameSession) {
    return null;
  }

  if (highestScoreGameSession.discountCode) {
    return highestScoreGameSession;
  }

  const res = await fetch(`/api/game-session/${highestScoreGameSession.id}`, {
    method: 'POST',
    body: JSON.stringify({
      id: highestScoreGameSession.id,
      ownershipToken: highestScoreGameSession.ownershipToken,
      email,
      hasApprovedMarketingConsent,
      isFirstRedemption
    })
  });

  if (!res.ok) {
    throw new Error('Could not redeem the discount!');
  }

  const responseData = await res.json();

  setLocalStorageJson(IS_FIRST_REDEMPTION_STORAGE_KEY, false);

  return saveHighestScoreGameSession({
    ...highestScoreGameSession,
    ...responseData
  });
};

export const getPublicGame = async (id: string) => {
  const res = await fetch(`/api/game-session/${id}`);

  if (!res.ok) throw new Error('Could not retrieve public game!');

  const responseData = await res.json();

  return responseData as PublicGameSession;
};
